// src/App.jsx
import { useEffect, useMemo, useState, useRef } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { CssBaseline, ThemeProvider } from '@mui/material';

import theme from '@/theme';
import { appRoutes } from '@/routes';
import { remountApp } from '@/utils/remountApp';
import { ROUTES } from '@/constants/routes';
import { fetchUserData, getEncryptedSSOData } from '@/services/ghlService';
import { setUserData } from '@/store/userSlice';
import { resetEntireStore } from '@/store/storeUtils';

import ErrorBoundary from '@/components/ErrorBoundary';
import Sidebar from '@/components/Sidebar/Sidebar';
import ProtectedRoute from '@/components/ProtectedRoute/ProtectedRoute';
import AppHeader from '@/components/AppHeader/AppHeader';

function App() {
    const [loading, setLoading] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const location = useLocation();
    const dispatch = useDispatch();

    const prevUserData = useSelector((state) => state.user.userData);

    const currentRoute = useMemo(() => {
        return appRoutes.find((route) => route.path === location.pathname);
    }, [location.pathname]);

    const CurrentIcon = currentRoute?.icon;
    const previousUserRef = useRef(prevUserData);

    useEffect(() => {
        async function loadUser() {
            try {
                setLoading(true);
                const encCode = await getEncryptedSSOData();

                const requestBody = { encryptedSSOData: encCode };

                const userDataResponse = await fetchUserData(requestBody);

                if (previousUserRef.current && userDataResponse) {
                    const userChanged =
                        previousUserRef.current.userId !==
                            userDataResponse.userId ||
                        previousUserRef.current.activeLocation !==
                            userDataResponse.activeLocation;

                    if (userChanged) {
                        await resetEntireStore(dispatch);

                        remountApp();
                    } else {
                        return;
                    }
                }

                previousUserRef.current = userDataResponse;

                dispatch(setUserData(userDataResponse));

                const { userId, activeLocation } = userDataResponse;
                if (!userId || !activeLocation) {
                    console.warn('User data is incomplete:', userDataResponse);
                }
            } finally {
                setLoading(false);
            }
        }

        loadUser();
    }, [dispatch]);

    return (
        <ErrorBoundary>
            <ThemeProvider theme={theme}>
                <CssBaseline />

                {currentRoute.path !== ROUTES.WEBRTC ? (
                    <div className="flex h-screen overflow-hidden">
                        <div className="h-full flex-shrink-0">
                            <Sidebar
                                loading={loading}
                                collapsed={collapsed}
                                toggleCollapsed={() =>
                                    setCollapsed((prev) => !prev)
                                }
                            />
                        </div>

                        <div className="flex-1 flex flex-col overflow-auto bg-gray-200">
                            <AppHeader
                                loading={loading}
                                CurrentIcon={CurrentIcon}
                                currentRoute={currentRoute}
                            />

                            <div className="flex flex-col px-4 h-screen">
                                <Routes>
                                    {appRoutes.map(({ path, element }) => (
                                        <Route
                                            key={path}
                                            path={path}
                                            element={
                                                <ProtectedRoute path={path}>
                                                    {element}
                                                </ProtectedRoute>
                                            }
                                        />
                                    ))}
                                </Routes>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div
                        style={{
                            width: '343px',
                            height: '637px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                        }} //The exact iframe dimension in GHL panel for webcall modal (343px * 637px)
                        className="h-screen"
                    >
                        <Routes>
                            {appRoutes.map(({ path, element }) => (
                                <Route
                                    key={path}
                                    path={path}
                                    element={
                                        <ProtectedRoute path={path}>
                                            {element}
                                        </ProtectedRoute>
                                    }
                                />
                            ))}
                        </Routes>
                    </div>
                )}
            </ThemeProvider>
        </ErrorBoundary>
    );
}

export default App;
